import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { addMonths, format } from 'date-fns';
import Decimal from 'decimal.js';
import produce from 'immer';
import { get as lodashGet, compact } from 'lodash-es';
import { InvoiceDisplayStatus, RevenueReportAggregateEnum } from 'anchor-scout-common/types';
import { extractClaimsFromToken, handlePromise, handleRequest, wait } from '~/infra/utils';
import { TimeActivitiesModel } from '~/models';
import { HistoricRevenueModel } from '~/models/HistoricRevenueModel';
import { ServiceTemplateType } from '~/models/service-template';
import { defaultAgreementStatusFilter } from '~/pages/AgreementsDashboard/agreementsDashboardFilters';
import { AccountingItemType } from '~/types/api/accounting-integration';
import { selectors } from './state-selectors';
import { defaultFeatureFlagsKey, getDefaultInitialState, getOrCreateStoreLocation, initMakeFetchAndStoreFunction } from './store.utils';
export const FUTURE_INVOICE_LOOKAHEAD_MONTHS = 16;
export function initStoreActions(param) {
    let { api, getState, setState, onReportToSentry, onLogBI, getNow } = param;
    const makeFetchAndStoreFunction = initMakeFetchAndStoreFunction({
        setState,
        getState,
        getNow
    });
    /** Resets the store state */ function reset(authFlowDone) {
        setState(getDefaultInitialState(_object_spread({}, getState().session), {
            authFlowDone,
            featureFlags: getState().ff.featureFlags
        }), false, 'reset');
    }
    /** Resets the business data. used when switching businesses  */ function resetBusinessData() {
        const _getDefaultInitialState = getDefaultInitialState(getState().session), { session: _1, authToken: _2, authFlowDone: _3, claims: _4, userInfo: _5, userClientData: _6, ff: _7 } = _getDefaultInitialState, resetData = _object_without_properties(_getDefaultInitialState, [
            "session",
            "authToken",
            "authFlowDone",
            "claims",
            "userInfo",
            "userClientData",
            "ff"
        ]);
        setState(resetData, false, 'resetBusinessData');
    }
    /**
     * Starts a new request. baseLocation is the key in the store or an array that
     * represents a path in the store.
     */ function requestStart(baseLocation) {
        setState(produce((state)=>{
            const loc = getOrCreateStoreLocation(state, baseLocation);
            loc.loading = true;
        }), false, "requestStart ".concat(JSON.stringify(baseLocation)));
    }
    /**
     * Ends a request successfully with response data. baseLocation is the key in the
     * store or an array that represents a path in the store.
     */ // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function requestSuccess(baseLocation, data) {
        setState(produce((state)=>{
            const loc = getOrCreateStoreLocation(state, baseLocation);
            loc.loading = false;
            loc.lastSuccessfulFetch = Date.now();
            loc.data = data;
            loc.error = null;
        }), false, "requestSuccess ".concat(JSON.stringify(baseLocation)));
    }
    /**
     * Ends a request with an error. baseLocation is the key in the store or an array that
     * represents a path in the store.
     */ // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function requestFail(baseLocation, error) {
        setState(produce((state)=>{
            const loc = getOrCreateStoreLocation(state, baseLocation);
            loc.loading = false;
            loc.data = null;
            loc.error = error;
        }), false, "requestFail ".concat(JSON.stringify(baseLocation)));
    }
    function setRelationshipVendorId(id) {
        setState({
            ff: _object_spread_props(_object_spread({}, getState().ff), {
                relationshipVendorId: id
            })
        }, false, 'setRelationshipVendorId');
    }
    /**
     * Fetches the feature-flags and saves in the store. If given a businessId will fetch
     * the feature-flags for that businessId. Otherwise, will fetch global feature-flags.
     */ async function fetchFeatureFlags(businessId, authToken) {
        const storeLocation = [
            'ff',
            'featureFlags',
            businessId || defaultFeatureFlagsKey
        ];
        const featureFlags = lodashGet(getState(), storeLocation);
        if (featureFlags && (featureFlags.loading || featureFlags.data)) {
            // No need to load twice if data exists or a request is already in progress.
            return;
        }
        requestStart(storeLocation);
        try {
            const featureFlags = await api.featureFlag.getAll(businessId, authToken);
            requestSuccess(storeLocation, featureFlags);
        } catch (err) {
            requestFail(storeLocation, err);
        }
    }
    async function resetStoreAndFinishAuthFlow() {
        reset();
        await fetchFeatureFlags();
        setState({
            authFlowDone: true
        });
    }
    async function setAuthToken(authToken) {
        const claims = extractClaimsFromToken(authToken);
        if (!claims) {
            return await resetStoreAndFinishAuthFlow();
        }
        if (selectors.isResetPasswordFlow({
            claims
        })) {
            await fetchFeatureFlags(selectors.user.businessId(getState()), authToken);
            setState({
                authToken,
                claims,
                authFlowDone: true
            });
            return;
        }
        setState({
            claims,
            authToken
        });
        const userInfoResponse = await handlePromise(api.auth.getUserInfo());
        if (!userInfoResponse.success) {
            await resetStoreAndFinishAuthFlow();
            throw new Error('Failed to get user info');
        }
        const userInfo = userInfoResponse.data;
        if (userInfo == null) {
            return await resetStoreAndFinishAuthFlow();
        }
        const isVendor = userInfo.isVendor();
        const hasBusiness = userInfo.hasBusiness();
        const businessIdFromUserInfo = userInfo.businessId();
        const businessIdFromClaims = (claims === null || claims === void 0 ? void 0 : claims.businessId) || undefined; // No business in claims can be an empty string, and we want it undefined to compare to userInfo.
        if (businessIdFromUserInfo !== businessIdFromClaims && !businessIdFromClaims) {
            onReportToSentry("authToken/me-query businessId mismatch. token:".concat(businessIdFromClaims, ", meQuery:").concat(businessIdFromUserInfo));
        }
        // We need to fetchIntegrations before setting userInfo to prevent a race condition where pages
        // are loaded before integrations are available when logging in to a concrete page that needs QBO data.
        await Promise.all(compact([
            isVendor && handlePromise(api.auth.getUserVideos().then(setUserVideos)),
            isVendor && businessIdFromUserInfo && handlePromise(api.business.getInfo(businessIdFromUserInfo).then(setBusinessInfo)),
            isVendor && handlePromise(fetchIntegrations()),
            handlePromise(fetchUserClientData()),
            businessIdFromUserInfo && handlePromise(fetchBusinessClientData(businessIdFromUserInfo)),
            businessIdFromUserInfo && fetchFeatureFlags(businessIdFromUserInfo),
            !hasBusiness && handlePromise(fetchTeamInvites())
        ]));
        setUserInfo(userInfo);
        // Necessary for the store to update before the next render cycle, so useFeatureFlags
        // will get the ones from the user's business instead of the default ones.
        await wait(0);
        setState({
            authFlowDone: true
        });
    }
    async function fetchUserInfo() {
        const userInfo = await api.auth.getUserInfo();
        setUserInfo(userInfo);
        return userInfo;
    }
    function setUserInfo(userInfo) {
        setState({
            userInfo
        }, false, 'setUserInfo');
    }
    const setUserVideos = (videos)=>{
        setState({
            videos
        }, false, 'videos');
    };
    const setBusinessInfo = (businessInfo)=>setState({
            businessInfo
        });
    const markHasSentAProposal = (isSampleRelationship)=>{
        const { businessInfo } = getState();
        if (!businessInfo || businessInfo.hasSentAProposal) return;
        setState({
            businessInfo: _object_spread({}, businessInfo, isSampleRelationship ? {
                hasSentASampleProposal: true
            } : {
                hasSentAProposal: true
            })
        });
    };
    function setWorksAt(worksAt) {
        setState((state)=>({
                userInfo: state.userInfo ? state.userInfo.setWorksAt(worksAt) : null
            }), false, 'setWorksAt');
    }
    function setUserClientData(userClientData) {
        setState({
            userClientData
        }, false, 'setUserClientData');
    }
    function setWorkspaces(workspaces) {
        setState((state)=>({
                userInfo: state.userInfo ? state.userInfo.setWorkspaceModels(workspaces) : null
            }), false, 'setWorkspaces');
    }
    function setBusinessClientData(data, _businessId) {
        const businessId = _businessId || selectors.user.businessId(getState());
        if (!businessId) return;
        setState({
            businessClientData: _object_spread_props(_object_spread({}, getState().businessClientData), {
                [businessId]: data
            })
        }, false, 'setBusinessClientData');
    }
    function setStripeLoadingFailed(stripeLoadingFailed) {
        setState({
            stripeLoadingFailed
        }, false, 'setStripeLoadingFailed');
    }
    function setInAppNotificationsDrawerOpen(inAppNotificationsDrawerOpen) {
        setState({
            inAppNotificationsDrawerOpen
        }, false, 'inAppNotificationsDrawerOpen');
        if (inAppNotificationsDrawerOpen) {
            setState({
                showNotificationPimple: false
            }, false, 'newNotificationsCounter');
        }
    }
    function setIntegration(integration) {
        const integrations = getState().integrations.data || [];
        const newIntegrations = integrations.filter((i)=>i.source !== integration.source).concat([
            integration
        ]);
        setState({
            integrations: _object_spread_props(_object_spread({}, getState().integrations), {
                data: newIntegrations
            })
        });
    }
    async function updateBusinessColor(color) {
        const worksAt = selectors.user.business(getState());
        if (worksAt) {
            await api.vendor.updateBusinessDetails(worksAt.id, {
                color
            });
            setWorksAt(Object.assign({}, worksAt, {
                branding: {
                    color
                }
            }));
        }
    }
    async function fetchPayouts() {
        requestStart('payouts');
        const payoutsData = await handleRequest(api.payout.getAll());
        if (!payoutsData.success) {
            requestFail('payouts', payoutsData.error);
            throw payoutsData.error;
        } else {
            requestSuccess('payouts', payoutsData.data);
            return payoutsData.data;
        }
    }
    async function fetchAgreements() {
        const businessId = selectors.user.businessId(getState());
        const isVendor = selectors.user.isVendor(getState());
        const storeLocation = [
            'agreements'
        ];
        if (!businessId) {
            return [];
        }
        requestStart(storeLocation);
        try {
            const agreements = await api.relationship.getSummaries({
                businessId,
                type: 'agreement',
                isVendor,
                filters: {
                    agreementStatus: defaultAgreementStatusFilter
                }
            });
            requestSuccess(storeLocation, agreements);
            return agreements;
        } catch (err) {
            requestFail(storeLocation, err);
            return [];
        }
    }
    async function fetchDraftsSummaries() {
        const storeLocation = 'drafts';
        requestStart(storeLocation);
        try {
            const drafts = await api.draft.getSummaries();
            requestSuccess(storeLocation, drafts);
        } catch (err) {
            requestFail(storeLocation, err);
        }
    }
    async function fetchAccountingCustomers(accounting) {
        const storeLocation = "".concat(accounting, "Customers");
        requestStart(storeLocation);
        try {
            const qboCustomers = await api.accounting.getCustomers({
                accounting
            });
            requestSuccess(storeLocation, qboCustomers);
            return qboCustomers;
        } catch (err) {
            requestFail(storeLocation, err);
        }
    }
    async function fetchAccountingItems(accounting, isServiceBundleEnabled, type) {
        const storeLocation = "".concat(accounting, "Items");
        requestStart(storeLocation);
        const defaultType = isServiceBundleEnabled ? [
            AccountingItemType.Service,
            AccountingItemType.Bundle
        ] : [
            AccountingItemType.Service
        ];
        try {
            const response = await api.accounting.getItems({
                accounting,
                type: type || defaultType
            });
            requestSuccess(storeLocation, response);
        } catch (err) {
            requestFail(storeLocation, err);
        }
    }
    async function fetchFinancialCentsItems() {
        const storeLocation = [
            'financialCents',
            'items'
        ];
        requestStart(storeLocation);
        try {
            const response = await api.financialCents.getItems();
            requestSuccess(storeLocation, response);
            return response;
        } catch (err) {
            requestFail(storeLocation, err);
            throw err;
        }
    }
    async function fetchKeeperItems() {
        const storeLocation = [
            'keeper',
            'items'
        ];
        requestStart(storeLocation);
        try {
            const response = await api.keeper.getItems();
            requestSuccess(storeLocation, response);
            return response;
        } catch (err) {
            requestFail(storeLocation, err);
            throw err;
        }
    }
    async function fetchKarbonItems() {
        const storeLocation = [
            'karbon',
            'items'
        ];
        requestStart(storeLocation);
        try {
            const response = await api.karbon.getItems();
            requestSuccess(storeLocation, response);
            return response;
        } catch (err) {
            requestFail(storeLocation, err);
            throw err;
        }
    }
    async function fetchClientHubItems() {
        const storeLocation = [
            'clientHub',
            'items'
        ];
        requestStart(storeLocation);
        try {
            const response = await api.clientHub.getItems();
            requestSuccess(storeLocation, response);
            return response;
        } catch (err) {
            requestFail(storeLocation, err);
            throw err;
        }
    }
    async function fetchFinancialCentsUsers() {
        const storeLocation = [
            'financialCents',
            'users'
        ];
        requestStart(storeLocation);
        try {
            const response = await api.financialCents.getUsers();
            requestSuccess(storeLocation, response);
            return response;
        } catch (err) {
            requestFail(storeLocation, err);
            throw err;
        }
    }
    async function fetchKarbonUsers() {
        const storeLocation = [
            'karbon',
            'users'
        ];
        requestStart(storeLocation);
        try {
            const response = await api.karbon.getUsers();
            requestSuccess(storeLocation, response);
            return response;
        } catch (err) {
            requestFail(storeLocation, err);
            throw err;
        }
    }
    async function fetchQboClasses() {
        const allowToCustomizeQboClasses = selectors.integration.allowToCustomizeQboClasses(getState());
        const storeLocation = 'qboClasses';
        if (!allowToCustomizeQboClasses) {
            requestSuccess(storeLocation, []);
            return;
        }
        try {
            requestStart(storeLocation);
            const response = await api.qbo.getClasses();
            requestSuccess(storeLocation, response);
        } catch (err) {
            requestFail(storeLocation, err);
        }
    }
    async function fetchQboTimeActivities(relationshipId) {
        const allowTimeActivities = selectors.integration.allowTimeActivities(getState());
        const storeLocation = [
            'qboTimeActivities',
            relationshipId
        ];
        if (!allowTimeActivities) {
            const timeActivities = new TimeActivitiesModel();
            requestSuccess(storeLocation, timeActivities);
            return timeActivities;
        }
        try {
            requestStart(storeLocation);
            const response = await api.qbo.getTimeActivities(relationshipId);
            requestSuccess(storeLocation, response);
            return response;
        } catch (err) {
            requestFail(storeLocation, err);
            return null;
        }
    }
    async function fetchProposals() {
        const businessId = selectors.user.businessId(getState());
        const isVendor = selectors.user.isVendor(getState());
        const storeLocation = 'proposals';
        if (!businessId) {
            return;
        }
        requestStart(storeLocation);
        try {
            const proposals = await api.relationship.getSummaries({
                businessId,
                type: 'proposal',
                isVendor
            });
            requestSuccess(storeLocation, proposals);
        } catch (err) {
            requestFail(storeLocation, err);
        }
    }
    async function fetchProposalTemplates() {
        const storeLocation = 'proposalTemplates';
        requestStart(storeLocation);
        try {
            const proposalTemplates = await api.proposalTemplates.getAll();
            requestSuccess(storeLocation, proposalTemplates);
        } catch (err) {
            requestFail(storeLocation, err);
        }
    }
    async function fetchMonthlyEarnings() {
        const storeLocation = 'monthlyEarnings';
        requestStart(storeLocation);
        try {
            const monthlyEarnings = await api.report.getMonthlyEarnings();
            requestSuccess(storeLocation, monthlyEarnings);
        } catch (err) {
            requestFail(storeLocation, err);
        }
    }
    async function fetchHistoricAgreementStats() {
        const businessId = selectors.user.businessId(getState());
        if (!businessId) return;
        const storeLocation = 'historicAgreementStats';
        requestStart(storeLocation);
        try {
            const historicAgreementStats = await api.report.getHistoricAgreementStats(businessId);
            requestSuccess(storeLocation, historicAgreementStats);
            return historicAgreementStats;
        } catch (err) {
            requestFail(storeLocation, err);
            throw err;
        }
    }
    async function fetchSalesSummary() {
        const storeLocation = 'salesSummary';
        requestStart(storeLocation);
        try {
            const salesSummary = await api.report.getSalesSummary();
            requestSuccess(storeLocation, salesSummary);
            return salesSummary;
        } catch (err) {
            requestFail(storeLocation, err);
            throw err;
        }
    }
    const fetchIssuedAndPaymentInitiatedTotal = async ()=>{
        const { REVENUE_TOTAL_AMOUNT } = selectors.ff.featureFlagsData(getState());
        if (REVENUE_TOTAL_AMOUNT) {
            const businessId = selectors.user.businessId(getState());
            if (!businessId) return new Decimal(0);
            const totalAmountInCents = await api.report.getIssuedTotalAmount(businessId);
            return new Decimal(totalAmountInCents).div(100);
        }
        const { invoices } = await api.invoice.getAll({
            status: [
                InvoiceDisplayStatus.Issued,
                InvoiceDisplayStatus.PaymentInitiated
            ]
        });
        return invoices.reduce((acc, invoice)=>acc.add(invoice.rawTotalAmount), new Decimal(0));
    };
    const fetchRevenueWidgetStats = async ()=>{
        const storeLocation = 'historicRevenueStats';
        const to = format(addMonths(getNow(), FUTURE_INVOICE_LOOKAHEAD_MONTHS), 'yyyy-MM-dd');
        requestStart(storeLocation);
        try {
            const [historicPaidInvoices, historicPaidExternallyInvoices, futureRevenueTotals, issuedTotal] = await Promise.all([
                api.reports.revenueReportV3({
                    end: undefined,
                    start: undefined,
                    aggregate: RevenueReportAggregateEnum.Month,
                    services: [],
                    customers: [],
                    breakdowns: []
                }),
                api.report.getHistoricPaidExternallyInvoices(),
                api.report.getFutureInvoiceTotals({
                    to
                }),
                fetchIssuedAndPaymentInitiatedTotal()
            ]);
            const historicRevenueStats = HistoricRevenueModel.fromDTO(futureRevenueTotals, historicPaidInvoices, historicPaidExternallyInvoices, issuedTotal, getNow());
            requestSuccess(storeLocation, historicRevenueStats);
            return historicRevenueStats;
        } catch (err) {
            requestFail(storeLocation, err);
            throw err;
        }
    };
    async function fetchInvoicesDigest() {
        const storeLocation = 'invoicesDigest';
        requestStart(storeLocation);
        try {
            const invoicesDigest = await api.invoice.getDigestFromQuery();
            requestSuccess(storeLocation, invoicesDigest);
            return invoicesDigest;
        } catch (err) {
            requestFail(storeLocation, err);
            throw err;
        }
    }
    const fetchPayoutsSummary = makeFetchAndStoreFunction({
        fetcher: api.payout.getSummary,
        getLoadableContent: (state)=>state.payoutsSummary
    });
    const dumbFetchPersonaKYBStatus = makeFetchAndStoreFunction({
        fetcher: api.vendor.getKYBStatus,
        getLoadableContent: (state)=>state.personaKYBStatus,
        allowStaleDataFor: 2000
    });
    function fetchPersonaKYBStatus() {
        const worksAt = selectors.user.business(getState());
        return worksAt ? dumbFetchPersonaKYBStatus(worksAt.id) : Promise.resolve(null);
    }
    const fetchIntegrations = makeFetchAndStoreFunction({
        fetcher: api.integration.getAll,
        getLoadableContent: (state)=>state.integrations,
        allowStaleDataFor: 2000
    });
    const fetchIntegrationsSummary = makeFetchAndStoreFunction({
        fetcher: api.integration.getSummary,
        getLoadableContent: (state)=>state.integrationsSummary,
        allowStaleDataFor: 2000
    });
    async function fetchKarbonIntegration() {
        const storeLocation = 'integrations';
        requestStart(storeLocation);
        try {
            const currentIntegrations = getState().integrations;
            if (!currentIntegrations.data) {
                return fetchIntegrations();
            }
            const integration = await api.karbon.getIntegration();
            const newIntegrations = currentIntegrations.data.filter((i)=>i.source !== 'karbon').concat([
                integration
            ]);
            requestSuccess(storeLocation, newIntegrations);
            return integration;
        } catch (err) {
            requestFail(storeLocation, err);
        }
    }
    async function fetchInAppNotifications() {
        requestStart('inAppNotifications');
        try {
            const { pushedNotifications, counter } = await api.inAppNotifications.getAll();
            requestSuccess('inAppNotifications', pushedNotifications);
            const inAppNotificationsDrawerOpen = getState().inAppNotificationsDrawerOpen;
            const shouldShowPimple = !inAppNotificationsDrawerOpen && Boolean(counter);
            setState({
                showNotificationPimple: shouldShowPimple
            }, false, 'newNotificationsCounter');
        } catch (err) {
            requestFail('inAppNotifications', err);
        }
    }
    async function fetchServiceTemplates() {
        const storeLocation = 'serviceTemplates';
        requestStart(storeLocation);
        try {
            const businessId = selectors.user.businessId(getState());
            const { active } = await api.serviceTemplate.getAll(businessId, {
                includeDeleted: false,
                type: [
                    ServiceTemplateType.Service
                ]
            });
            const ret = {
                active
            };
            requestSuccess(storeLocation, ret);
            return ret;
        } catch (err) {
            requestFail(storeLocation, err);
            return null;
        }
    }
    async function fetchBundles() {
        const storeLocation = 'bundles';
        requestStart(storeLocation);
        try {
            const businessId = selectors.user.businessId(getState());
            const { active } = await api.serviceTemplate.getAll(businessId, {
                includeDeleted: false,
                type: [
                    ServiceTemplateType.Bundle
                ]
            });
            const ret = {
                active
            };
            requestSuccess(storeLocation, ret);
            return ret;
        } catch (err) {
            requestFail(storeLocation, err);
            return null;
        }
    }
    async function fetchUserClientData() {
        const data = await api.user.getUserClientData();
        setUserClientData(data);
        return data;
    }
    async function fetchBusinessClientData(businessId) {
        const data = await api.user.getBusinessClientData();
        setBusinessClientData(data, businessId);
        return data;
    }
    /**
     * @deprecated: use useLegalTerms hook instead of this function
     *
     */ const dumbFetchLegalTerms = makeFetchAndStoreFunction({
        fetcher: ()=>api.business.getLegalTerms(),
        getLoadableContent: (state)=>state.legalTerms
    });
    /**
     * @deprecated: use useLegalTerms hook instead of this function
     *
     */ const fetchLegalTerms = ()=>dumbFetchLegalTerms().catch(()=>null);
    async function updateBusinessClientData(businessId, changes) {
        let options = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : {};
        const { useLocalData } = options;
        const currentData = useLocalData ? selectors.user.businessClientData(getState(), businessId) : await fetchBusinessClientData();
        const newData = _object_spread({}, currentData, changes);
        await api.user.setBusinessClientData(newData);
        setBusinessClientData(newData);
        return newData;
    }
    async function fetchActivityLog(relationshipId) {
        const storeLocation = [
            'activityLog',
            relationshipId
        ];
        requestStart(storeLocation);
        try {
            const res = await api.activityLog.get(relationshipId);
            requestSuccess(storeLocation, res);
            return res;
        } catch (err) {
            requestFail(storeLocation, err);
        }
    }
    async function updateUserClientData(changes) {
        let options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
        const { useLocalData } = options;
        const currentData = useLocalData ? getState().userClientData : await fetchUserClientData();
        const newData = _object_spread({}, currentData, changes);
        await api.user.setUserClientData(newData);
        setUserClientData(newData);
        return newData;
    }
    /** Logs a BI event. */ function logBI(id, data) {
        const state = getState();
        onLogBI(id, _object_spread({
            userId: selectors.user.id(state),
            businessId: selectors.user.businessId(state)
        }, data || {}));
    }
    function setBranding(branding) {
        var _getState_userInfo;
        setState({
            userInfo: ((_getState_userInfo = getState().userInfo) === null || _getState_userInfo === void 0 ? void 0 : _getState_userInfo.setBranding(branding)) || null
        }, false, 'setBranding');
    }
    function setQueryAsLoading(queryKey, isLoading) {
        setState({
            isQueryLoading: _object_spread_props(_object_spread({}, getState().isQueryLoading), {
                [queryKey]: isLoading
            })
        }, false, "setQueryAsHardLoading ".concat(queryKey, " ").concat(isLoading));
    }
    function setLocationStack(locations) {
        setState({
            locationStack: locations
        }, false, 'setLocationStack');
    }
    function setTeamInvites(teamInvites) {
        setState({
            teamInvites: _object_spread_props(_object_spread({}, getState().teamInvites), {
                data: teamInvites
            })
        }, false, 'setTeamInvites');
    }
    async function fetchTeamInvites() {
        const storeLocation = 'teamInvites';
        try {
            requestStart(storeLocation);
            const response = await api.user.getTeamInvites();
            requestSuccess(storeLocation, response);
            return response;
        } catch (err) {
            requestFail(storeLocation, err);
            return null;
        }
    }
    const createContact = (newContact)=>{
        const { CLIENT_CONTACT_V2_API } = selectors.ff.featureFlagsData(getState());
        return CLIENT_CONTACT_V2_API ? api.contact.createV2(newContact) : api.contact.create(newContact);
    };
    const getContactById = (contactId)=>{
        const { CLIENT_CONTACT_V2_API } = selectors.ff.featureFlagsData(getState());
        return CLIENT_CONTACT_V2_API ? api.contact.getByIdV2(contactId) : api.contact.getById(contactId);
    };
    return {
        reset,
        resetBusinessData,
        requestStart,
        requestSuccess,
        requestFail,
        setAuthToken,
        fetchUserInfo,
        setUserVideos,
        setUserInfo,
        setWorksAt,
        setWorkspaces,
        setUserClientData,
        setStripeLoadingFailed,
        setInAppNotificationsDrawerOpen,
        setIntegration,
        setBusinessInfo,
        markHasSentAProposal,
        updateBusinessColor,
        fetchPayouts,
        fetchAgreements,
        fetchDraftsSummaries,
        fetchAccountingCustomers,
        fetchAccountingItems,
        fetchQboClasses,
        fetchQboTimeActivities,
        fetchProposals,
        fetchProposalTemplates,
        fetchMonthlyEarnings,
        fetchHistoricAgreementStats,
        fetchSalesSummary,
        fetchRevenueWidgetStats,
        fetchPayoutsSummary,
        fetchPersonaKYBStatus,
        fetchIntegrations,
        fetchIntegrationsSummary,
        fetchInAppNotifications,
        fetchServiceTemplates,
        fetchBundles,
        fetchUserClientData,
        fetchBusinessClientData,
        fetchActivityLog,
        /**
         * @deprecated: use `useLegalTerms` hook instead of this function
         */ fetchLegalTerms,
        fetchFinancialCentsItems,
        fetchKeeperItems,
        fetchFinancialCentsUsers,
        fetchKarbonUsers,
        updateBusinessClientData,
        updateUserClientData,
        logBI,
        setBranding,
        fetchKarbonItems,
        fetchKarbonIntegration,
        fetchClientHubItems,
        fetchInvoicesDigest,
        ff: {
            setRelationshipVendorId,
            fetchFeatureFlags
        },
        api,
        createContact,
        getContactById,
        setQueryAsLoading,
        setLocationStack,
        fetchTeamInvites,
        setTeamInvites,
        setEnableIntercom: (enableIntercom)=>setState({
                enableIntercom
            }, false, 'setEnableIntercom')
    };
}
